/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 *
 */

// Allowed range for the clock-in window (recordTimeout), in minutes; when provided it must be greater than 0 //
export const RECORD_TIMEOUT_LIMITS = { MIN: 1, MAX: 720 };

// Allowed range for the tolerance (range), in minutes (up to a full day) //
export const TOLERANCE_LIMITS = { MIN: 1, MAX: 1440 };

// Allowed extensionDays range, independent of shift type //
export const EXTENSION_DAYS_LIMITS = { MIN: 0, MAX: 3 };

// Max shift duration (start -> end, in hours) allowed //
export const MAX_SHIFT_HOURS = 24;

// When the idle gap outside the shift (dead zone) is smaller than this, the record
// window falls back to a fixed default instead of half the gap //
export const SMALL_DEAD_ZONE_MINUTES = 3 * 60; // 3 hours
export const DEFAULT_RECORD_TIMEOUT = 180; // minutes

export const ATTENDANCE_DEPARTMENT_SUPERVISOR_MODEL ={
    attendanceDepartmentId: null,
    supervisorId: null,
    toRegister: function (attendanceDepartmentId, selectedList) {
        return selectedList.map(e => ({
            attendanceDepartmentId: attendanceDepartmentId,
            supervisorId: e.userId,
        }))
    }
}

export const DEPARTMENT_POLICY_MODEL = {
    id: null,
    departmentId: null,
    supervisor: [],
    startShiftAt: '',
    endShiftAt: '',
    range: null,
    extensionDays: null,
    recordTimeout: null,
    // Overnight shift = start at or after end (crosses midnight, including a full 24h shift where start === end).
    // Times are zero-padded HH:mm:ss, so string compare is safe. //
    isOvernightShift: function () {
        return this.startShiftAt !== '' && this.endShiftAt !== '' && this.startShiftAt >= this.endShiftAt;
    },
    // Diurnal shift = start before end (same day). //
    isDiurnalShift: function () {
        return this.startShiftAt !== '' && this.endShiftAt !== '' && this.startShiftAt < this.endShiftAt;
    },
    hasRecordTimeout: function () {
        return this.recordTimeout !== null && this.recordTimeout !== '' && this.recordTimeout !== undefined;
    },
    // recordTimeout is required for overnight shifts //
    requiresRecordTimeout: function () {
        return this.isOvernightShift();
    },
    // Shift duration in hours from start to end; overnight and 24h shifts wrap past midnight //
    shiftDurationHours: function () {
        if (!this.startShiftAt || !this.endShiftAt) {
            return 0;
        }
        const [sh, sm] = this.startShiftAt.split(':').map(Number);
        const [eh, em] = this.endShiftAt.split(':').map(Number);
        let diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff <= 0) {
            diff += 24 * 60;
        }
        return diff / 60;
    },
    shiftDurationMinutes: function () {
        return this.shiftDurationHours() * 60;
    },
    // Max clock-in window (recordTimeout): half of the idle gap outside the shift
    // (endShiftAt -> startShiftAt, wrapping midnight), so consecutive windows never
    // overlap. In minutes. With no times set the whole day is free -> 720. //
    maxRecordTimeout: function () {
        const deadZone = 24 * 60 - this.shiftDurationMinutes();
        // Small dead zone (< 3h, e.g. 00:00 -> 23:59): default the window to 180 min.
        // Otherwise it's half the idle gap so consecutive windows never overlap. //
        if (deadZone < SMALL_DEAD_ZONE_MINUTES) {
            return DEFAULT_RECORD_TIMEOUT;
        }
        return Math.floor(deadZone / 2);
    },
    // A shift cannot span more than MAX_SHIFT_HOURS //
    isShiftDurationValid: function () {
        return this.shiftDurationHours() <= MAX_SHIFT_HOURS;
    },
    // Start and end cannot be the same time (e.g. 00:00 -> 00:00) //
    hasDistinctStartEnd: function () {
        return !(this.startShiftAt !== '' && this.endShiftAt !== '' && this.startShiftAt === this.endShiftAt);
    },
    // Overnight shifts cross midnight, so the checkout must roll into the next day:
    // they require at least one extension day (> 0). Diurnal shifts may use 0. //
    requiresExtensionDay: function () {
        return this.isOvernightShift();
    },
    isExtensionDaysValidForShift: function () {
        return !this.requiresExtensionDay() || Number(this.extensionDays) > 0;
    },
    // recordTimeout, when provided, must be within its limits: at least MIN and no
    // more than half the idle gap outside the shift (so windows don't overlap) //
    isRecordTimeoutInLimits: function () {
        return !this.hasRecordTimeout()
            || (Number(this.recordTimeout) >= RECORD_TIMEOUT_LIMITS.MIN && Number(this.recordTimeout) <= this.maxRecordTimeout());
    },
    // The tolerance (range) must be within its allowed minute limits //
    isRangeInLimits: function () {
        return !(this.range > 0)
            || (Number(this.range) >= TOLERANCE_LIMITS.MIN && Number(this.range) <= TOLERANCE_LIMITS.MAX);
    },
    // The tolerance (range) cannot exceed the shift duration (end - start) //
    isRangeWithinShift: function () {
        if (!this.startShiftAt || !this.endShiftAt) {
            return true;
        }
        return !(this.range > 0) || Number(this.range) <= this.shiftDurationMinutes();
    },
    isValid: function () {
        const hasTimes = this.startShiftAt !== '' && this.endShiftAt !== '';

        if (!hasTimes || !(this.range > 0)) {
            return false;
        }

        // recordTimeout is required for overnight shifts, optional for plain diurnal/24h shifts; when provided it must be within range //
        const recordTimeoutInRange = this.hasRecordTimeout() && this.isRecordTimeoutInLimits();
        const hasValidRecordTimeout = this.requiresRecordTimeout()
            ? recordTimeoutInRange
            : this.isRecordTimeoutInLimits();

        // extensionDays is always required; its range is validated separately by validateShifts //
        const hasExtensionDays = this.extensionDays !== null && this.extensionDays !== '';

        return hasValidRecordTimeout && hasExtensionDays && this.isExtensionDaysValidForShift()
            && this.isShiftDurationValid() && this.hasDistinctStartEnd()
            && this.isRangeInLimits() && this.isRangeWithinShift();
    },
    normalizeValue: function (name, value) {
        if (name === 'range') {
            const numericValue = Number(value);
            return value === '' || numericValue === 0 ? null : numericValue;
        }
        // recordTimeout allows 0 as a valid value; only an empty field maps to null //
        if (name === 'recordTimeout') {
            return value === '' ? null : Number(value);
        }
        return value;
    },
    toAddEdit: function (supervisors) {
        return {
            id: this.id,
            startShiftAt: this.startShiftAt,
            endShiftAt: this.endShiftAt,
            range: this.range,
            departmentId: this.departmentId,
            extensionDays: this.extensionDays ?? 0,
            recordTimeout: this.recordTimeout ?? null,
            supervisors: supervisors?.length > 0 ? supervisors.map(e => ({supervisorId: e.userId})) : []
        };
    },
    clear: function () {
        this.id = null;
        this.departmentId = null;
        this.supervisor = [];
        this.startShiftAt = '';
        this.endShiftAt = '';
        this.range = null;
        this.extensionDays = null;
        this.recordTimeout = null;
    },
    validateShifts: function () {
        // Overnight shifts require at least one extension day (including an empty value) //
        if (!this.isExtensionDaysValidForShift()) {
            return false;
        }
        if (this.extensionDays === null || this.extensionDays === '') {
            return true;
        }
        const extension = parseInt(this.extensionDays);

        // extensionDays must stay within the allowed range (independent of shift type) //
        if (extension < EXTENSION_DAYS_LIMITS.MIN || extension > EXTENSION_DAYS_LIMITS.MAX) {
            return false;
        }

        return true;
    }
};
